import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppService } from '@core/services/app.service';
import { StorageService } from '@core/services/storage.service';
import { entitlements } from '@env/entitlements';
import { paths } from '@env/paths';
import { TranslocoService } from '@ngneat/transloco';
import { Menu } from '@shared/models/Menu';
import { Spinner } from '@shared/models/spinner';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Application } from './shared/models/Application';
import { EnvironmentService } from '@core/services/environment.service';
import { EntitlementsLoadedService } from '@core/services/entitlements-loaded.service';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterContentChecked {
  public application!: Application;
  public showNavigation = false;
  dataMang!: string;
  isLoading$!: Observable<boolean>;
  loadingTitle!: string;
  loadingSubtitle!: string;
  entitlementLoaded: boolean = false;

  constructor(
    private readonly router: Router,
    private readonly appService: AppService,
    private readonly translocoService: TranslocoService,
    private readonly storageService: StorageService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly entitlementsLoadedService: EntitlementsLoadedService
  ) {
    this.storageService.clearData();
    router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe(() => {
        this.showNavigation =
          this.router.routerState.snapshot.url !== '/' + paths.common.notfound;
      });

    this.application = new Application(
      'FCS-C',
      'FCS-C',
      'assets/logo/FCSC-logo.svg',
      []
    );
  }

  translatePage() {
    this.translocoService
      .selectTranslate('components.navbar.data-management')
      .subscribe((resp) => {
        this.dataMang = resp;
        this.application.menus = [
          new Menu(
            this.dataMang,
            'uploadtool',
            true,
            [entitlements.VUS.FCSC_VORAB_DATA],
            [

              new Menu(
                this.translocoService.translate(
                  'components.navbar.preliminary-data'
                ),
                'uploadtool/vorab',
                false,
                [entitlements.VUS.FCSC_VORAB_DATA]
              ),
              new Menu(
                this.translocoService.translate(
                  'components.navbar.series-data'
                ),
                'uploadtool/serien',
                false,
                [entitlements.VUS.FCSC_SERIEN_DATA],
                [
                  new Menu(
                    this.translocoService.translate(
                      'components.navbar.series-bdp'
                    ),
                    'uploadtool/serien/serien-bdp',
                    false,
                    [entitlements.VUS.FCSC_SERIEN_DATA]
                  ),
                  new Menu(
                    this.translocoService.translate(
                      'components.navbar.series-upload-list'
                    ),
                    'uploadtool/serien/upload-list',
                    false,
                    [entitlements.VUS.FCSC_SERIEN_DATA]
                  ),
                ]
              ),
            ],
            'uploadtool/vorab'
          ),
          new Menu(
            this.translocoService.translate(
              'components.navbar.expert-function'
            ),
            'experttool',
            false,
            [entitlements.VUS.FLASHWARE_CALCULATION],
            [
              new Menu(
                this.translocoService.translate(
                  'components.navbar.flashware-detection'
                ),
                'experttool/flashware',
                false,
                [entitlements.VUS.FLASHWARE_CALCULATION]
              ),
              new Menu(
                this.translocoService.translate(
                  'components.navbar.log-history'
                ),
                'experttool/log-history',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA]
              ),
            ],
            'experttool/flashware'
          ),

          new Menu(
            this.translocoService.translate('components.navbar.admin-section'),
            'admin',
            false,
            [entitlements.VUS.FCSC_MANAGEMENT_DATA],
            [
              new Menu(
                this.translocoService.translate(
                  'components.navbar.series-config'
                ),
                'admin/series-config',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA]
              ),
              new Menu(
                this.translocoService.translate('components.navbar.code-list'),
                'admin/code-list',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA]
              ),
              new Menu(
                this.translocoService.translate(
                  'components.navbar.notification-management'
                ),
                'admin/notification-management',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA]
              ),
              new Menu(
                'FSF Logs',
                'admin/fsf-logs',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA]
              ),
              new Menu(
                this.translocoService.translate(
                  'components.navbar.vp5-versions'
                ),
                'admin/vp-versions',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA]
              ),
              new Menu(
                this.translocoService.translate(
                  'components.navbar.global-data'
                ),
                'admin/globalData/TCFGDionameBL',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA],
                [
                  new Menu(
                    this.translocoService.translate('components.navbar.TCFGDionameBL'),
                    'admin/globalData/TCFGDionameBL',
                    false,
                    [entitlements.VUS.FCSC_MANAGEMENT_DATA],
                  )
                ]
              ),
              new Menu(
                // TODO: Add translation for PreFlash Monitoring
                'PreFlash Monitoring',
                'admin/preflash-monitoring',
                false,
                [entitlements.VUS.FCSC_MANAGEMENT_DATA],
              )
            ],
            'admin/series-config'
          )
        ];
      });
  }

  ngOnInit(): void {
    const currentUrl = this.router.url;
    if (currentUrl && currentUrl !== '/') {
      this.showNavigation = currentUrl !== '/' + paths.common.notfound;
    }

    this.translatePage();

    this.isLoading$ = this.appService.spinnerSubject.pipe(
      map((spinnerObject: Spinner) => spinnerObject.isLoading)
    );
    this.appService?.spinnerSubject?.subscribe((spinnerObject: Spinner) => {
      this.loadingTitle = spinnerObject.loadingTitle;
      this.loadingSubtitle = spinnerObject.loadingSubtitle;
    });

    this.entitlementsLoadedService.entitlementsLoadedEvent$.subscribe(flag => {
      this.entitlementLoaded = flag;
      this.changeDetector.detectChanges();
    });

    if (this.storageService.getItem('fcsc-access-token')) {
      this.entitlementsLoadedService.emitEntitlementsLoadedEvent(true);
    }
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }
}